import { Component, DestroyRef, inject, Input, OnChanges, signal, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, UntypedFormArray } from '@angular/forms';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { AiKnowledgeService } from '../../../ai-knowledge.service';
import {
  KnowledgeSource,
  KnowledgeSourceConfig,
  KnowledgeSourceConfigWebsiteScrapeConfig,
  KnowledgeSourceConfigWebsiteScrapeConfigScrapedPage,
  KnowledgeSourceTraining,
  TrainingProgress,
} from '@vendasta/embeddings';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { map } from 'rxjs';
import { AiKnowledgeI18nModule } from '../../../assets/i18n/ai-knowledge-i18n.module';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { AiLoadingSpinnerComponent } from '../../../shared/ai-loading-spinner/ai-loading-spinner.component';

export const STEP_OPTION = 'scrapedPages';

@Component({
  selector: 'ai-knowledge-scraped-pages',
  imports: [
    CommonModule,
    TranslateModule,
    AiKnowledgeI18nModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    FormsModule,
    GalaxyFormFieldModule,
    GalaxyEmptyStateModule,
    GalaxyLoadingSpinnerModule,
    MatExpansionModule,
    MatIcon,
    AiLoadingSpinnerComponent,
  ],
  providers: [AiKnowledgeService],
  templateUrl: './scraped-pages.component.html',
  styleUrls: ['./scraped-pages.component.scss'],
})
export class ScrapedPagesComponent implements OnChanges {
  @Input() knowledgeSource: KnowledgeSource;
  private readonly knowledgeService = inject(AiKnowledgeService);
  destroyRef = inject(DestroyRef);
  form: FormGroup;
  websiteScrapeConfig: KnowledgeSourceConfigWebsiteScrapeConfig;
  trainingInfo: KnowledgeSourceTraining;
  readonly isLoading = signal(true);
  delayProgressScreenClosure = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.knowledgeSource) {
      this.websiteScrapeConfig = this.knowledgeSource?.config?.websiteScrapeConfig;
      this.trainingInfo = this.knowledgeSource?.training;

      if (!this.knowledgeSource || !this.websiteScrapeConfig.scrapedPages) return;

      this.websiteScrapeConfig?.scrapedPages?.sort((a, b) =>
        a?.trainingEnabled === b?.trainingEnabled ? a?.url?.localeCompare(b?.url) : a?.trainingEnabled ? -1 : 1,
      );

      this.form = new FormGroup({
        scrapedPages: new FormArray([]),
      });
      const controls = this.websiteScrapeConfig?.scrapedPages?.map((page) => new FormControl(page.trainingEnabled));
      this.form.controls.scrapedPages = new FormArray(controls);
      this.form.controls.scrapedPages.valueChanges
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          map(() => this.form.markAsDirty()),
        )
        .subscribe();

      this.isLoading.set(false);
    }
  }

  get scrapedPagesArray(): UntypedFormArray {
    return this.form.controls['scrapedPages'] as UntypedFormArray;
  }

  setAllScrapedPages(value: boolean): void {
    this.form.controls.scrapedPages = new FormArray(
      this.websiteScrapeConfig?.scrapedPages?.map((p) => {
        const training = p?.errorMessage ? false : value;
        return new FormControl(training);
      }),
    );
    this.form.markAsDirty();
  }

  getSuccessfullyScrapedPagesCount(): number {
    return this.websiteScrapeConfig?.scrapedPages?.filter((p) => !p?.errorMessage).length || 0;
  }

  getErroredScrapedPages(): KnowledgeSourceConfigWebsiteScrapeConfigScrapedPage[] {
    return this.websiteScrapeConfig?.scrapedPages?.filter((p) => p?.errorMessage);
  }

  getErroredScrapedPagesCount(): number {
    return this.getErroredScrapedPages().length || 0;
  }

  hasUnsavedChanges(): boolean {
    return this.form?.dirty;
  }

  async submit(): Promise<string> {
    this.scrapedPagesArray.controls.forEach((pageControl, index) => {
      this.websiteScrapeConfig.scrapedPages[index].trainingEnabled = pageControl.value;
    });
    const config = {
      websiteScrapeConfig: this.websiteScrapeConfig,
    } as KnowledgeSourceConfig;
    if (this.knowledgeSource) {
      const source = this.knowledgeSource;
      source.config = config;
      await this.knowledgeService.updateKnowledgeSourceNameAndConfig(source);
      this.form.markAsPristine();
      return source.id;
    }
    throw new Error('No knowledge source');
  }

  getTrainingProgressTitle(): string {
    if (this.knowledgeSource.training.progress === TrainingProgress.TRAINING_QUEUED) {
      return 'EDIT_KNOWLEDGE.TRAINING.TRAINING_QUEUED';
    } else {
      if (this.getTrainingProgressCount() > 0) {
        return 'EDIT_KNOWLEDGE.TRAINING.TRAINING_IN_PROGRESS';
      } else {
        return 'EDIT_KNOWLEDGE.TRAINING.TRAINING_QUEUED';
      }
    }
  }

  getTrainingProgressCount(): number {
    let trainingEnabledPageCount = 0;
    for (const page of this.websiteScrapeConfig.scrapedPages) {
      if (page.trainingEnabled) {
        trainingEnabledPageCount++;
      }
    }

    if (this.trainingInfo.totalUnits > 0 && this.trainingInfo.completedUnits > 0) {
      return Math.round((this.trainingInfo.completedUnits / this.trainingInfo.totalUnits) * trainingEnabledPageCount);
    } else {
      return 0;
    }
  }

  protected readonly TrainingProgress = TrainingProgress;
  protected readonly Math = Math;
}
